const R = 6371; // km

function distToSegmentDeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function findInsertionIndex(lat, lng, waypoints) {
  const valid = waypoints
    .map((wp, i) => ({ wp, i }))
    .filter(({ wp }) => wp.lat != null && wp.lng != null);
  if (valid.length < 2) return waypoints.length;
  let minDist = Infinity, bestIdx = valid[valid.length - 1].i + 1;
  for (let j = 0; j < valid.length - 1; j++) {
    const a = valid[j].wp, b = valid[j + 1].wp;
    const d = distToSegmentDeg(lat, lng, a.lat, a.lng, b.lat, b.lng);
    if (d < minDist) { minDist = d; bestIdx = valid[j + 1].i; }
  }
  return bestIdx;
}

/**
 * Nearest point on a set of polylines, in metres (local equirectangular).
 * polylines: [[[lat,lng],...], ...]. Returns {lat,lng} or null if nothing
 * is within maxDistM. Fast enough for per-mousemove use (brute force).
 */
export function nearestPointOnPolylines(lat, lng, polylines, maxDistM) {
  const mx = 111320 * Math.cos((lat * Math.PI) / 180); // metres per deg lng
  const my = 110540;                                   // metres per deg lat
  let best = null;
  let bestD2 = maxDistM * maxDistM;

  for (const line of polylines) {
    for (let i = 0; i < line.length - 1; i++) {
      const ax = (line[i][1]     - lng) * mx, ay = (line[i][0]     - lat) * my;
      const bx = (line[i + 1][1] - lng) * mx, by = (line[i + 1][0] - lat) * my;
      const dx = bx - ax, dy = by - ay;
      const len2 = dx * dx + dy * dy;
      let t = len2 > 0 ? -(ax * dx + ay * dy) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const px = ax + t * dx, py = ay + t * dy;
      const d2 = px * px + py * py;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = { lat: lat + py / my, lng: lng + px / mx };
      }
    }
  }
  return best;
}

/**
 * Chaikin corner-cutting: smooths a polyline by repeatedly replacing each
 * segment with two points at 25 % and 75 %.  First and last points are
 * preserved so the route still starts and ends exactly where expected.
 * @param {[number,number][]} coords  Array of [lon, lat] (GeoJSON order)
 * @param {number} iterations         2–4 gives a clean result without blurring detail
 */
export function chaikinSmooth(coords, iterations = 3) {
  if (coords.length < 3) return coords;
  let pts = coords;
  for (let k = 0; k < iterations; k++) {
    const out = [pts[0]];
    for (let i = 0; i < pts.length - 1; i++) {
      const [x0, y0] = pts[i];
      const [x1, y1] = pts[i + 1];
      out.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1]);
      out.push([0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }
    out.push(pts[pts.length - 1]);
    pts = out;
  }
  return pts;
}

export function destinationPoint(lat, lng, bearingDeg, distKm) {
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lng * Math.PI) / 180;
  const θ  = (bearingDeg * Math.PI) / 180;
  const δ  = distKm / R;
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: (φ2 * 180) / Math.PI, lng: (((λ2 * 180) / Math.PI) + 540) % 360 - 180 };
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function bearingBetween(lat1, lng1, lat2, lng2) {
  const φ1 = (lat1 * Math.PI) / 180, φ2 = (lat2 * Math.PI) / 180;
  const dλ = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Generate loop via-points (NOT including from/to endpoints).
 *
 * Closed loop (from ≈ to): square inscribed in a circle pointing in bearingDeg.
 *   Each leg of the square diverges at 90° from the previous — wide enough to
 *   force distinct roads and prevent U-turns.
 *
 * Open detour (from ≠ to): perpendicular arc bulging away from the direct line.
 *   3 points on a sin-curve bulge — one at 25 %, peak at 50 %, one at 75 %.
 *   `bearingDeg` controls which side to bulge (null = auto: left of from→to).
 *
 * Distance is advisory only — loop shape is the priority.
 */
export function generateLoopViaPoints(fromLat, fromLng, toLat, toLng, targetDistKm, bearingDeg = null, viaCount = 4) {
  const sep = haversineKm(fromLat, fromLng, toLat, toLng);
  return sep < 0.05
    ? closedLoopVias(fromLat, fromLng, targetDistKm, bearingDeg, viaCount)
    : openArcVias(fromLat, fromLng, toLat, toLng, targetDistKm, bearingDeg);
}

// --- closed loop (regular polygon) -------------------------------------------
// vias = N-1 points evenly spaced on a circle. More vias (higher curviness)
// pack the loop tighter, forcing the router onto winding connector roads;
// fewer let it take direct arterials. Distance is advisory, spacing is priority.

function closedLoopVias(lat, lng, targetDistKm, bearingDeg, viaCount = 4) {
  const N = Math.max(3, viaCount + 1); // polygon sides; vias = N - 1
  const bearing = bearingDeg ?? Math.random() * 360;
  const r = targetDistKm / (N * 2 * Math.sin(Math.PI / N));
  const centre = destinationPoint(lat, lng, bearing, r);

  return Array.from({ length: N - 1 }, (_, i) => {
    const angle = ((bearing + 180) - (i + 1) * (360 / N) + 720) % 360;
    const pt = destinationPoint(centre.lat, centre.lng, angle, r);
    return { lat: pt.lat, lng: pt.lng, name: 'Via', bearing: bearingBetween(lat, lng, pt.lat, pt.lng) };
  });
}

// Score a POI by how interesting it is likely to be for a motorcycle route.
// Higher = better. Viewpoints are the best: they're explicitly flagged scenic by
// OSM contributors and are always on winding/elevated roads to reach them.
export function interestScore(p) {
  const t = p.tags || {};
  if (t.tourism === 'viewpoint') return 3.0;
  if (t.natural === 'waterfall') return 2.5;
  if (t.mountain_pass === 'yes') return 2.2;
  if (t.natural === 'peak') return 2.0 + Math.min((parseFloat(t.ele) || 0) / 1000, 1.0);
  if (t.natural === 'saddle' || t.natural === 'cape' || t.natural === 'headland') return 1.8;
  if (t.natural === 'cliff') return 1.6;
  if (t.historic === 'lighthouse' || t.historic === 'castle') return 1.5;
  if (t.place === 'village' || t.place === 'hamlet') return 1.4;
  if (t.amenity === 'cafe') return 1.2;
  return 1.0;
}

/**
 * Build loop via-points progressively, stepping around the compass.
 *
 * Rather than picking all points radially from origin and hoping the router
 * can connect them, this builds a chain: each waypoint is selected from the
 * *previous* waypoint's position in the sector that advances the loop by
 * one step. This means the routing from V[i] → V[i+1] always goes forward —
 * it can never require backtracking because the next point is always "ahead."
 *
 * If no POI exists in a sector, a geometric point is placed in the correct
 * direction so the loop stays well-shaped without falling back to a pentagon.
 */
export function pickSeedBearing(places, fromLat, fromLng, targetDistKm, count = 4) {
  const perLeg = targetDistKm / (count + 1);
  const firstLeg = places
    .map(p => ({
      bearing:  bearingBetween(fromLat, fromLng, p.lat, p.lng),
      dist:     haversineKm(fromLat, fromLng, p.lat, p.lng),
      interest: interestScore(p),
    }))
    .filter(p => p.dist >= perLeg * 0.3 && p.dist <= perLeg * 1.5)
    .sort((a, b) => b.interest - a.interest);
  return firstLeg.length > 0 ? firstLeg[0].bearing : Math.random() * 360;
}

/**
 * Fraction of route length traversed more than once (0 = no retracing).
 * Segments are keyed on ~11 m grid so opposite directions on the same road match.
 * @param {[number,number][]} coords  GeoJSON [lon, lat] coordinates
 */
export function overlapScore(coords) {
  if (!coords || coords.length < 2) return 0;
  const seen = new Set();
  let total = 0, dup = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i], b = coords[i + 1];
    const len = haversineKm(a[1], a[0], b[1], b[0]);
    total += len;
    const ka = `${a[0].toFixed(4)},${a[1].toFixed(4)}`;
    const kb = `${b[0].toFixed(4)},${b[1].toFixed(4)}`;
    const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    if (seen.has(key)) dup += len;
    else seen.add(key);
  }
  return total > 0 ? dup / total : 0;
}

export function buildDirectionalVias(places, fromLat, fromLng, targetDistKm, count = 4, opts = {}) {
  const perLeg = targetDistKm / (count + 1);

  const initialBearing = opts.initialBearing ?? pickSeedBearing(places, fromLat, fromLng, targetDistKm, count);
  const direction = opts.direction ?? 1; // 1 = clockwise step order, -1 = counterclockwise

  const stepAngle = (360 / (count + 1)) * direction;
  const vias = [];
  const used = new Set();
  let cur = { lat: fromLat, lng: fromLng };

  for (let i = 0; i < count; i++) {
    // Target compass direction for this step of the loop
    const targetBearing = ((initialBearing + (i + 1) * stepAngle) % 360 + 360) % 360;

    // Find POIs within the forward sector from the *current* position
    const candidates = places
      .map((p, idx) => {
        const dist    = haversineKm(cur.lat, cur.lng, p.lat, p.lng);
        const brg     = bearingBetween(cur.lat, cur.lng, p.lat, p.lng);
        const angDiff = Math.abs(((brg - targetBearing + 540) % 360) - 180);
        return { ...p, idx, dist, brg, angDiff, interest: interestScore(p) };
      })
      .filter(p =>
        !used.has(p.idx) &&
        p.dist >= perLeg * 0.25 &&
        p.dist <= perLeg * 1.8 &&
        p.angDiff <= 55          // within a ±55° forward cone
      )
      .sort((a, b) => b.interest - a.interest);

    let via;
    if (candidates.length > 0) {
      const pick = candidates[0];
      used.add(pick.idx);
      via = {
        lat:     pick.lat,
        lng:     pick.lng,
        name:    pick.name || 'Via',
        bearing: bearingBetween(fromLat, fromLng, pick.lat, pick.lng),
      };
    } else {
      // No POI in this sector — place a geometric point in the correct direction.
      // Using the current position as base keeps each geometric step sized correctly.
      const pt = destinationPoint(cur.lat, cur.lng, targetBearing, perLeg);
      via = {
        lat:     pt.lat,
        lng:     pt.lng,
        name:    'Via',
        bearing: bearingBetween(fromLat, fromLng, pt.lat, pt.lng),
      };
    }

    vias.push(via);
    cur = { lat: via.lat, lng: via.lng };
  }

  return vias;
}

/**
 * Fan `count` target points at `distKm` from an origin, spread across bearings.
 * Used by the open route generator to propose destinations in different
 * directions. Full circle by default; pass a baseBearing + spreadDeg to bias
 * the fan forward (e.g. when extending an existing route).
 */
export function fanDestinations(fromLat, fromLng, distKm, count, opts = {}) {
  const { baseBearing = Math.random() * 360, spreadDeg = 360 } = opts;
  const full = spreadDeg >= 360;
  const step = full ? 360 / count : spreadDeg / Math.max(1, count - 1);
  const startB = full ? baseBearing : baseBearing - spreadDeg / 2;
  return Array.from({ length: count }, (_, i) => {
    const b = (((startB + i * step) % 360) + 360) % 360;
    const pt = destinationPoint(fromLat, fromLng, b, distKm);
    return { lat: pt.lat, lng: pt.lng, bearing: b };
  });
}

// --- open detour (arc) -------------------------------------------------------
// Two points at 1/3 and 2/3 along the route with a generous perpendicular bulge.
// Wider spacing between vias gives OSRM more room to route cleanly.

function openArcVias(fromLat, fromLng, toLat, toLng, targetDistKm, bearingDeg) {
  const direct = bearingBetween(fromLat, fromLng, toLat, toLng);
  const perp = bearingDeg ?? ((direct + 90) % 360);
  const r = targetDistKm / 3; // larger bulge for better spacing

  return [1 / 3, 2 / 3].map(t => {
    const baseLat = fromLat + t * (toLat - fromLat);
    const baseLng = fromLng + t * (toLng - fromLng);
    const bulge = r * Math.sin(t * Math.PI);
    const pt = destinationPoint(baseLat, baseLng, perp, bulge);
    return { lat: pt.lat, lng: pt.lng, name: 'Via', bearing: bearingBetween(fromLat, fromLng, pt.lat, pt.lng) };
  });
}
